import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("dermatrix_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Whether an error means the SESSION is over (log out) vs merely a forbidden
 * ACTION (show an error, stay logged in).
 *
 * - 401 → always a session failure (token missing/expired/invalid, or revoked
 *   server-side). Log out.
 * - 403 → almost always "you're logged in but not allowed to do THIS" (e.g. a
 *   telecaller hitting an admin-only endpoint, which the role-gated UI does
 *   routinely). Do NOT log out — treating these as session-ended silently wiped
 *   valid tokens and dumped people back to the login screen. The exceptions
 *   are a deactivated or blacklisted account, which the backend reports as 403
 *   "Account disabled" / "Account blacklisted. Contact your administrator.".
 *   Mirrors _isSessionEnded in the app's dio_client.dart.
 * - no response at all (network blip, backend restarting mid-deploy) → NOT a
 *   session failure. Keep the token.
 */
export function isSessionEnded(err) {
  const status = err?.response?.status;
  if (status === 401) return true;
  return accountBlockedDetail(err) != null;
}

/** The server's message when the account itself was switched off (403
 * "Account disabled" / "Account blacklisted…"), else null. */
export function accountBlockedDetail(err) {
  if (err?.response?.status !== 403) return null;
  const detail = String(err?.response?.data?.detail ?? "");
  // Match the start only. A blacklisted LEAD's 403 is "This lead is blacklisted:
  // {reason}…" with a free-text reason — `includes` would log the viewer out if
  // someone typed "account disabled" into that reason. Mirrors dio_client.dart.
  const d = detail.trim().toLowerCase();
  return d.startsWith("account disabled") || d.startsWith("account blacklisted") ? detail : null;
}

// A blocked account mid-session: the login page shows this message after the
// forced logout (read + cleared by Login.jsx).
export const LOGOUT_REASON_KEY = "dermatrix_logout_reason";
export const SESSION_ENDED_EVENT = "dermatrix:session-ended";

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (isSessionEnded(err)) {
      localStorage.removeItem("dermatrix_token");
      const blocked = accountBlockedDetail(err);
      // The login call itself shows its own error inline — only a request made
      // during a session forces the logout + carries the reason to /login.
      const isLoginCall = String(err?.config?.url || "").includes("/auth/login");
      if (blocked && !isLoginCall) {
        sessionStorage.setItem(LOGOUT_REASON_KEY, blocked);
        window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
